"use client";

import { useState } from "react";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  ComboPanel,
  HookePanel,
  OscillatorPanel,
} from "@/components/tools/physics-fields/hooke-panels";
import {
  type SigChoice,
  SigFigsField,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const HOOKES_LAW_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modeSpring: "Spring force F = kx",
  modeCombo: "Springs in series or parallel",
  modeOscillator: "Mass on a spring",
  exampleLabel: "Start from an example",
  custom: "Custom values",
  hookeExamples: {
    stretch: "A 200 N/m spring stretched 5 cm",
    compress: "A 5 kN/m spring compressed 2 cm",
    measure: "Find k: a 1 kg load stretches a spring 4.9 cm",
  },
  comboExamples: {
    two: "Two 100 N/m springs carrying 10 N",
    three: "Three springs: 100, 200 and 300 N/m",
  },
  oscillatorExamples: {
    hundred: "0.25 kg on a 100 N/m spring, 5 cm amplitude",
    eighty: "500 g on an 80 N/m spring, 4 cm amplitude",
  },
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  force: "Applied force F",
  forceShort: "Force F",
  stiffness: "Spring constant k",
  stiffnessShort: "Spring constant k",
  extension: "Extension x",
  extensionShort: "Extension x",
  extensionHint:
    "Positive to stretch, negative to compress. Measured from the spring's natural length.",
  appliedForce: "Force applied",
  energy: "Elastic energy U = ½kx²",
  restoring: "Restoring force −F",
  state: "The spring is",
  stretched: "Stretched",
  compressed: "Compressed",
  relaxed: "At its natural length",
  arrangement: "How are the springs joined?",
  series: "In series (end to end)",
  parallel: "In parallel (side by side)",
  springsHeading: "Spring constants",
  springLabel: "Spring {n} constant k",
  removeSpring: "Remove spring {n}",
  remove: "Remove",
  addSpring: "Add a spring",
  stiffnessUnit: "Unit for the spring constants",
  load: "Applied force on the combination (optional)",
  loadUnit: "Unit for the load",
  equivalent: "Equivalent spring constant",
  totalExtension: "Total extension",
  totalEnergy: "Total energy stored",
  shareCaption: "How the load divides among the springs.",
  colSpring: "Spring",
  colStiffness: "Constant k",
  colForce: "Force carried",
  colExtension: "Extension",
  colEnergy: "Energy",
  mass: "Mass m",
  massShort: "Mass m",
  period: "Period T",
  periodShort: "Period T",
  amplitude: "Amplitude A (optional)",
  amplitudeHint: "The largest displacement from equilibrium.",
  frequency: "Frequency f = 1/T",
  angularFrequency: "Angular frequency ω",
  maxSpeed: "Largest speed Aω",
  maxAcceleration: "Largest acceleration Aω²",
  oscillationEnergy: "Total energy ½kA²",
  sigFigs: "Significant figures",
  sigFigsMessage:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  working: "The working",
  graphCaption:
    "The force grows in a straight line with extension; the shaded triangle is the stored energy.",
  graphLabel: "Force against extension for a spring",
  graphDescription:
    "A straight line from the origin showing force F increasing in proportion to extension x, with a point marked on it and the triangle beneath it shaded to represent the stored energy.",
  graphEnergy: "U = ½ F x",
  comboLabel: "Springs joined together",
  seriesCaption:
    "In series every spring carries the same force and the extensions add, so the combination is softer than any one spring.",
  parallelCaption:
    "In parallel every spring stretches by the same amount and the forces add, so the combination is stiffer than any one spring.",
  seriesDescription:
    "A wall on the left, then springs joined end to end in a line, with an arrow F pulling on the last one.",
  parallelDescription:
    "Two vertical bars joined by springs stacked side by side, with an arrow F pulling the right-hand bar.",
  oscillationCaption:
    "The mass moves as a cosine; one full cycle takes the period T, and the period does not depend on the amplitude A.",
  oscillationLabel: "Displacement of a mass on a spring over time",
  oscillationDescription:
    "A cosine curve of displacement x against time t, over two cycles, with the amplitude A marked on the vertical axis and one period T marked below.",
  errorMissing:
    "Fill in every box except the one you are solving for. Scientific notation such as 2.5e3 works.",
  errorPositive:
    "The spring constant, mass and period must be greater than zero, and the amplitude cannot be negative.",
  errorZeroExtension:
    "With no extension the force is zero whatever the spring constant, so k cannot be found. Enter a non-zero extension.",
  errorSign:
    "A stretch needs a pulling force and a compression a pushing force: the force and the extension must have the same sign.",
  errorRange:
    "Those numbers are too large or too small to calculate. Check the units.",
  assumptions:
    "Assumes an ideal, massless spring within its elastic limit, where force is exactly proportional to extension. Beyond the limit a real spring stays deformed and the law fails. For a spring hung vertically the period is unchanged; only the equilibrium position shifts.",
} as const;

type Mode = "spring" | "combo" | "oscillator";

export default function HookesLawCalculatorWidget() {
  const strings = useToolUi(HOOKES_LAW_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("spring");
  const [sigChoice, setSigChoice] = useState<SigChoice>("4");
  const sig = Number(sigChoice);

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="hl-mode"
        onChange={setMode}
        options={[
          { value: "spring", label: strings.modeSpring },
          { value: "combo", label: strings.modeCombo },
          { value: "oscillator", label: strings.modeOscillator },
        ]}
        value={mode}
      />
      {mode === "spring" ? <HookePanel sig={sig} strings={strings} /> : null}
      {mode === "combo" ? <ComboPanel sig={sig} strings={strings} /> : null}
      {mode === "oscillator" ? (
        <OscillatorPanel sig={sig} strings={strings} />
      ) : null}
      <SigFigsField
        format={fmt}
        id="hl-sig"
        label={strings.sigFigs}
        locale={locale}
        message={strings.sigFigsMessage}
        onChange={setSigChoice}
        value={sigChoice}
      />
      <Notice>{strings.assumptions}</Notice>
    </>
  );
}
